import { css, injectGlobal, keyframes } from 'emotion';

const convertToRem = (value, baseValue = 16) => {
    const remValue = value / baseValue;
    if (remValue === 0) {
        return 0;
    }
    return `${remValue}rem`;
};

export const remCalc = (...values) => {
    const max = values.length;
    if (max === 1) {
        return convertToRem(values);
    }
    return values.map((value) => convertToRem(value)).join(' ');
};

const palettes = {
    primary: {
        mainDivider: 'rgb(72, 72, 72)',
        mainNav: '#151515',
        mainBackground: '#151515',
        mainPageBackground: 'rgb(38, 38, 38)',
        mainSubMenuBackground: 'rgb(43, 43, 43)',
    },
    secondary: {
        arteOrange: '#FD4600',
        rgaaOrange: 'rgba(219, 60, 0, 0.85)',
        concert: '#FF8BCA',
        cinema: '#979D9E',
        series: '#00E0E0',
        culture: '#BF55EC',
        histoire: '#FBBA16',
        voyages: '#26C281',
        science: '#DB0A5B',
        actu: '#1978D4',
        facebook: '#3B5998',
    },
    black: {
        base: 'rgb(0, 0, 0)', // #000000 palette(black)
    },
    grey: {
        light: 'rgb(247, 247, 247)', // #F7F7F7 palette(grey, light)
        base: 'rgb(238, 238, 238)', // #EEEEEE palette(grey)
        lightDark: 'rgb(229, 229, 229)', // #E5E5E5 palette(grey, light-dark)
        midDark: 'rgb(180, 180, 180)', // #B4B4B4 palette(grey, mid-dark)
        dark: 'rgb(159, 159, 159)', // #9F9F9F palette(grey, dark)
        xDark: 'rgb(114, 114, 114)', // #727272 palette(grey, x-dark)
        xxDark: 'rgb(40, 40, 40)', // #282828 palette(grey, xx-dark)
        menuDark: 'rgb(32, 32, 32)', // #202020 palette(grey, menu-dark)
        xxxDark: 'rgb(10, 10, 10)', // #0A0A0A palette(grey, xxx-dark)
        xxxDarkAlpha: 'rgba(10, 10, 10, 0.8)',
    },
    white: {
        base: 'rgb(255, 255, 255)', // #FFFFFF palette(white)
        alpha: 'rgba(255, 255, 255, .5)', // #FFFFFF palette(white)
        denseAlpha: 'rgba(255, 255, 255, .8)', // #FFFFFF palette(white)
    },
    arte: {
        base: 'rgb(250, 72, 28)', // #FA481C palette(arte)
    },
    orange: {
        light: 'rgb(250, 155, 132)', // #FA9B84 palette(orange, light)
        base: 'rgb(250, 128, 90)', // #FA805A palette(orange)
        dark: 'rgb(250, 100, 64)', // #FA6440 palette(orange, dark)
    },
    red: {
        base: 'rgb(255, 0, 0)', // #FF0000 palette(red)
    },
    blue: {
        light: 'rgb(124, 176, 255)', // #7CB0FF palette(blue, light)
        base: 'rgb(0, 170, 255)', // #00AAFF palette(blue)
        dark: 'rgb(28, 122, 186)', // #1C7ABA palette(blue, dark)
    },
    pink: {
        base: 'rgb(253, 139, 200)', // #FD8BC8 palette(pink)
    },
    green: {
        base: 'rgb(93, 210, 160)', // #5DD2A0 palette(green)
    },
};

export const palette = (type, tone = 'base') => palettes[type][tone];

export const breakpointSizes = {
    small: [0, 0],
    smallLarge: [28.749, 28.75], // smartphone portrait
    medium: [47.999, 48], // tablet portait
    mediumLarge: [61.999, 62], // intermediate breakpoint
    large: [63.999, 64], // tablet landscape
    xlarge: [74.999, 75], // desktop
    xxlarge: [89.999, 90], // not used in next
};

const MEDIA_FROM = (size) => `@media only screen and (min-width: ${size}em)`;
const MEDIA_TO = (size) => `@media only screen and (max-width: ${size}em)`;
const MEDIA_FROM_TO = (from, to) => `@media only screen and (min-width: ${from}em) and (max-width: ${to}em)`;

const generateMediaQueries = (sizes) => {
    const queries = {};
    Object.keys(sizes).forEach((size) => {
        const [to, from] = sizes[size];
        queries[size] = MEDIA_FROM(from);
        queries[`${size}Down`] = MEDIA_TO(to);
    });
    return queries;
};

export const mediaQueries = generateMediaQueries(breakpointSizes);

export const mediaQueryBuilder = (breakpointFrom, breakpointTo) => {
    if (!breakpointSizes[breakpointFrom]) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn(`Breakpoint ${breakpointFrom} doesn't exist.`);
        }
        return '';
    }
    if (breakpointTo) {
        return MEDIA_FROM_TO(
            breakpointSizes[breakpointFrom][1],
            breakpointSizes[breakpointTo] && breakpointSizes[breakpointTo][0],
        );
    }

    return MEDIA_FROM(breakpointSizes[breakpointFrom][1]);
};

export const sizeMap = {
    xxs: 16 * 0.8,
    xs: 16,
    s: 20,
    m: 25,
    l: 31,
    xl: 40,
    xxl: 48,
};

// font styles
export const font = {
    family: {
        body: "'barna', Arial, sans-serif",
        header: "'barna-stencil', Arial, sans-serif",
    },
    loaded: {
        body: false,
        header: false,
    },
    size: {
        xxs: remCalc(sizeMap.xxs),
        xs: remCalc(sizeMap.xs),
        s: remCalc(sizeMap.s),
        m: remCalc(sizeMap.m),
        l: remCalc(sizeMap.l),
        xl: remCalc(sizeMap.xl),
        xxl: remCalc(sizeMap.xxl),
    },
    weight: {
        thin: 200,
        regular: 400,
        medium: 600,
        bold: 800,
    },
    lineHeight: {
        xxs: remCalc(sizeMap.xxs * 1.1),
        xs: remCalc(sizeMap.xs * 1.1),
        s: remCalc(sizeMap.s * 1.1),
        m: remCalc(sizeMap.m * 1.1),
        l: remCalc(sizeMap.l * 1.1),
        xl: remCalc(sizeMap.xl * 1.1),
        xxl: remCalc(sizeMap.xxl * 1.1),
    },
};

export const importBodyFont = `
    @font-face {
      font-family: 'barna';
      src: url('//static-cdn.arte.tv/static/fonts/barna-web/woff2/Barna-Light.woff2') format('woff2'), url('//static-cdn.arte.tv/static/fonts/barna-web/woff/Barna-Light.woff') format('woff');
      font-weight: 200;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'barna';
      src: url('//static-cdn.arte.tv/static/fonts/barna-web/woff2/Barna-Regular.woff2') format('woff2'), url('//static-cdn.arte.tv/static/fonts/barna-web/woff/Barna-Regular.woff') format('woff');
      font-weight: 400;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'barna';
      src: url('//static-cdn.arte.tv/static/fonts/barna-web/woff2/Barna-SemiBold.woff2') format('woff2'), url('//static-cdn.arte.tv/static/fonts/barna-web/woff/Barna-SemiBold.woff') format('woff');
      font-weight: 600;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'barna';
      src: url('//static-cdn.arte.tv/static/fonts/barna-web/woff2/Barna-Bold.woff2') format('woff2'), url('//static-cdn.arte.tv/static/fonts/barna-web/woff/Barna-Bold.woff') format('woff');
      font-weight: 800;
      font-style: normal;
      font-display: swap;
    }
`;

export const importHeaderFont = `
    @font-face {
     font-family: 'barna-stencil';
     src: url('//static-cdn.arte.tv/static/fonts/barna-stencil-web/woff2/BarnaStencil-Regular.woff2') format('woff2'), url('//static-cdn.arte.tv/static/fonts/barna-stencil-web/woff/BarnaStencil-Regular.woff') format('woff');
     font-weight: normal;
     font-style: normal;
     font-display: swap;
   }
   @font-face {
     font-family: 'barna-stencil';
     src: url('//static-cdn.arte.tv/static/fonts/barna-stencil-web/woff2/BarnaStencil-Bold.woff2') format('woff2'), url('//static-cdn.arte.tv/static/fonts/barna-stencil-web/woff/BarnaStencil-Bold.woff') format('woff');
     font-weight: bold;
     font-style: normal;
     font-display: swap;
   }
`;

export const importFont = (kind, importCallback) => {
    if (!font.family[kind] || !importCallback) {
        return;
    }

    if (!font.loaded[kind]) {
        switch (kind) {
            case 'body':
                font.loaded.body = true;
                importCallback(importBodyFont);
                return;
            case 'header':
                font.loaded.header = true;
                importCallback(importHeaderFont);
                return;
            default:
                console.warn(`Font kind '${kind}' not found`);
        }
    }
};

export const injectFont = (kind) => importFont(kind, injectGlobal);

// icon sizes
export const icon = {
    size: {
        xxsmall: { width: remCalc(10), height: remCalc(10) },
        xsmall: { width: remCalc(15), height: remCalc(15) },
        small: { width: remCalc(20), height: remCalc(20) },
        mediumSmall: { width: remCalc(25), height: remCalc(25) },
        medium: { width: remCalc(30), height: remCalc(30) }, // default
        large: { width: remCalc(40), height: remCalc(40) },
        xlarge: { width: remCalc(50), height: remCalc(50) },
        xxlarge: { width: remCalc(60), height: remCalc(60) },
        full: { width: '100%', height: '100%' },
    },
};

// logo sizes
export const logo = {
    size: {
        xxxsmall: { width: remCalc(10), height: remCalc(40) },
        xxsmall: { width: remCalc(15), height: remCalc(60) },
        xsmall: { width: remCalc(20), height: remCalc(80) },
        small: { width: remCalc(25), height: remCalc(100) },
        medium: { width: remCalc(40), height: remCalc(160) },
        large: { width: remCalc(50), height: remCalc(200) },
        xlarge: { width: remCalc(75), height: remCalc(300) },
        xxlarge: { width: remCalc(100), height: remCalc(400) },
        headerSmall: { width: remCalc(20), height: remCalc(34) },
        headerMedium: { width: remCalc(20), height: remCalc(46) },
    },
};

export const srOnly = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
};

export const componentReset = {
    '*': {
        boxSizing: 'border-box',
        outline: 'none',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
    },
    a: {
        textDecoration: 'none',
    },
};

// section paddings
export const section = {
    padding: {
        mobile: remCalc(15),
        desktop: remCalc(50),
    },
};

// header
export const header = {
    height: {
        mobile: remCalc(40),
        desktop: remCalc(56),
    },
};

// global
export const globalWidth = remCalc(1600);
export const columnGutter = {
    small: remCalc(8),
    medium: remCalc(16),
    large: remCalc(24),
};

// interchange mixin to manage switch between thumbnail and original picture
export const interchange = {
    position: 'relative',
    '& img': {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        transition: 'opacity 0.35s, transform 0.35s',
    },
};

export const animations = {
    // shineOnMe mixin to add loading effect on teaser placeholder
    shine: keyframes`
        from {
            transform: translateX(-100%);
        }
        to {
            transform: translateX(100%);
        }
    `,
    blink: keyframes`
        from {
            opacity: 1;
        }
        to {
            opacity: 0;
        }
    `,
};

export const shineOnMe = (shineColor) => ({
    position: 'relative',
    overflow: 'hidden',
    '::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        animation: `${animations.shine} 2s cubic-bezier(.32, .89, .38, .25) infinite`,
        background: `linear-gradient(to right, transparent 0%, transparent 40%, ${shineColor} 50%, transparent 60%, transparent 100%)`,
    },
});

// hover mixins to add hover effects on teaser
export const teaserHoverOff = (direction = 'bottom', position = 0, opacity = 0.3, midPoint = 0) => {
    const reversePosition = 100 - position;
    const style = {
        overflow: 'hidden',
        '::after': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: `linear-gradient(to ${direction}, rgba(0, 0, 0, ${opacity}) ${midPoint}%, rgba(0, 0, 0, 0) 100%)`,
            transition: 'transform ease 350ms',
        },
    };
    switch (direction) {
        case 'bottom':
            style['::after'].left = 0;
            style['::after'].top = `${-reversePosition - 1}%`;
            break;
        case 'top':
            style['::after'].left = 0;
            style['::after'].bottom = `${-reversePosition}%`;
            break;
        case 'right':
            style['::after'].top = 0;
            style['::after'].left = `${-reversePosition}%`;
            break;
        case 'left':
            style['::after'].top = 0;
            style['::after'].right = `${-reversePosition}%`;
            break;
        default:
            break;
    }
    return style;
};

export const teaserHoverOn = (direction = 'bottom', position = 0) => {
    const reversePosition = 100 - position;
    const style = {
        '::after': {},
    };
    switch (direction) {
        case 'bottom':
            style['::after'].transform = `translate3d(0, ${reversePosition}%, 0)`;
            break;
        case 'top':
            style['::after'].transform = `translate3d(0, ${-reversePosition}%, 0)`;
            break;
        case 'right':
            style['::after'].transform = `translate3d(${reversePosition}%, 0, 0)`;
            break;
        case 'left':
            style['::after'].transform = `translate3d(${-reversePosition}%, 0, 0)`;
            break;
        default:
            break;
    }
    return style;
};

export const teaserFocusOn = (theme, value) => {
    const style = {
        '::before': {},
    };
    switch (value) {
        case 'basic':
            style['::before'] = {
                content: '""',
                position: 'absolute',
                width: '100%',
                height: '100%',
                zIndex: 1,
                borderStyle: 'solid',
                borderWidth: remCalc(2),
                borderColor: 'transparent',
            };
            break;
        case 'focused':
            style['::before'] = {
                borderColor: theme.teaser ? theme.global.borderColor : palette('white'),
                transition: 'border-color 0.3s linear',
            };
            break;
        default:
            break;
    }
    return style;
};

export const focuses = {
    rgaa: (stateSelector = ':focus,:hover', stylesOnActive = {}) => ({
        borderColor: 'transparent',
        borderStyle: 'solid',
        borderWidth: remCalc(2),
        [stateSelector]: {
            color: palette('white'),
            borderColor: palette('white'),
            transition: 'border-color 0.3s linear',
            ...stylesOnActive,
        },
    }),
    text: {
        basic: {
            borderBottomColor: 'transparent',
            borderBottomStyle: 'solid',
            borderBottomWidth: remCalc(1),
        },
        focused: {
            borderBottomColor: palette('white'),
            color: palette('white'),
            transition: 'border-color 0.3s linear',
        },
    },
};

export const hovers = {
    basic: {
        off: ['bottom', 0, 0.3],
        on: ['bottom', 0],
    },
    main: {
        off: ['right', 50, 0.5],
        on: ['right', 50],
    },
    overlay: {
        off: ['bottom', 70, 0.5],
        on: ['bottom', 70],
    },
    subscribe: {
        off: ['bottom', 101, 0.5],
        on: ['bottom', 200],
    },
    overlayRGAA: {
        off: ['bottom', 90, 0.6, 10],
        on: ['bottom', 90],
    },
    mainRGAA: {
        off: ['right', 90, 0.6, 20],
        on: ['right', 90],
    },
};

export const colors = {
    arte: palette('secondary', 'arteOrange'),
    facebook: palette('secondary', 'facebook'),
    isWhite: palette('white'),
    darkGrey: palette('grey', 'dark'),
    ARS: palette('secondary', 'concert'),
    CIN: palette('secondary', 'cinema'),
    SER: palette('secondary', 'series'),
    CPO: palette('secondary', 'culture'),
    HIS: palette('secondary', 'histoire'),
    DEC: palette('secondary', 'voyages'),
    SCI: palette('secondary', 'science'),
    ACT: palette('secondary', 'actu'),
};

/**
 * @param StyledComponent StyledSlideWrapper Styled compoenent wrapper
 * @param Object Styled components (with emotion) map for previous and next arrow buttons styling.
 */
export const touchScroll = (StyledSlideWrapper, { prev, next }) => {
    const arrowRules = (component) => ({
        [component]: {
            display: 'none',

            [mediaQueries.xlarge]: {
                display: 'flex',
            },
        },
    });

    return {
        ...(prev ? arrowRules(prev) : {}),
        ...(next ? arrowRules(next) : {}),

        [StyledSlideWrapper]: {
            overflowX: 'scroll',
            overflowY: 'hidden',
            '-webkit-overflow-scrolling': 'touch',

            [mediaQueries.xlarge]: {
                overflowX: 'visible',
                overflowY: 'visible',
                '-webkit-overflow-scrolling': 'auto',
            },
        },
    };
};

/**
 * Create a style instance for component (to be injected in className prop)
 *      Show only for the breakpoint and upper
 * @param string breakpoint The breakpoint aimed
 */
export const showFor = (breakpoint) =>
    css({
        [mediaQueries[`${breakpoint}Down`]]: {
            display: 'none',
        },
    });

/**
 * Create a style instance for component (to be injected in className prop)
 *      Hide for the breakpoint and upper
 * @param string breakpoint The breakpoint aimed
 */
export const hideFor = (breakpoint) =>
    css({
        [mediaQueries[breakpoint]]: {
            display: 'none',
        },
    });
